import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import _ from 'lodash';
import {
  InvestisseurFicheObjectif,
  InvestisseurObjectifRecueil,
  ObjectifReferentielFiche,
} from 'projects/fiche-conseil-lib/src/public-api';
import { lastValueFrom } from 'rxjs';

const allObjectifs = gql`
  query allObjectifs {
    minotaureObjectifsReferentiel {
      actif
      code
      libelle
      categorieCode
      categorieLibelle
    }
  }
`;

const investisseurObjectifsFieldsFragment = gql`
  fragment investisseurObjectifsFieldsFragment on InvestisseurObjectifRecueil {
    id
    objectifCode
    objectifLibelle
    categorieCode
    categorieLibelle
  }
`;

const allInvObjectifsByInvId = gql`
  query allInvObjectifsByInvId($investisseurId: Long!) {
    minotaureInvestisseurObjectifs(investisseurId: $investisseurId) {
      ...investisseurObjectifsFieldsFragment
    }
  }
  ${investisseurObjectifsFieldsFragment}
`;
const allInvObjectifsByInvIdAndCoInvId = gql`
  query allInvObjectifsByInvIdAndCoInvId($investisseurId: Long!, $coInvestisseurId: Long!) {
    objectifsInvestisseur: minotaureInvestisseurObjectifs(investisseurId: $investisseurId) {
      ...investisseurObjectifsFieldsFragment
    }
    objectifsCoInvestisseur: minotaureInvestisseurObjectifs(investisseurId: $coInvestisseurId) {
      ...investisseurObjectifsFieldsFragment
    }
  }
  ${investisseurObjectifsFieldsFragment}
`;

@Injectable({
  providedIn: 'root',
})
export class ObjectifsService {
  constructor(private queryManager: QueryManagerService) {}
  showKYCSelectedOnly: boolean = true;
  async getAllObjectifs(): Promise<ObjectifReferentielFiche[]> {
    const result = await lastValueFrom(
      this.queryManager.query<{ minotaureObjectifsReferentiel: ObjectifReferentielFiche[] }>({
        query: allObjectifs,
      })
    );
    return result.data.minotaureObjectifsReferentiel;
  }

  async getInvestisseurObjectifs(investisseurId: number, coInvestisseurId?: number): Promise<InvestisseurObjectifRecueil[]> {
    let investisseurObjectifs: InvestisseurObjectifRecueil[];

    if (!coInvestisseurId) {
      const result = await lastValueFrom(
        this.queryManager.query<{ minotaureInvestisseurObjectifs: InvestisseurObjectifRecueil[] }>({
          query: allInvObjectifsByInvId,
          variables: {
            investisseurId: investisseurId,
          },
          fetchPolicy: 'network-only',
        })
      );
      investisseurObjectifs = result.data.minotaureInvestisseurObjectifs;
    } else {
      const result = await lastValueFrom(
        this.queryManager.query<{
          objectifsInvestisseur: InvestisseurObjectifRecueil[];
          objectifsCoInvestisseur: InvestisseurObjectifRecueil[];
        }>({
          query: allInvObjectifsByInvIdAndCoInvId,
          variables: {
            investisseurId: investisseurId,
            coInvestisseurId: coInvestisseurId,
          },
          fetchPolicy: 'network-only',
        })
      );
      // return investisseur Objectif that match with coInv Objectifs.
      const objectifsInvestisseurTypeCode = result.data.objectifsInvestisseur.map(
        obj => obj.objectifCode
      );
      const objectifsCoInvestisseurTypeCode = result.data.objectifsCoInvestisseur.map(
        obj => obj.objectifCode
      );
      const sharedObjectifTypeCodes = objectifsInvestisseurTypeCode.filter(investisseurObjectif =>
        objectifsCoInvestisseurTypeCode.includes(investisseurObjectif)
      );
      const shared: InvestisseurObjectifRecueil[] = result.data.objectifsInvestisseur.filter(obj =>
        sharedObjectifTypeCodes.includes(obj.objectifCode)
      );
      investisseurObjectifs = shared;
    }

    //filter out the objectifs to remove duplicates
    investisseurObjectifs = _.uniqWith(
      investisseurObjectifs,
      (a, b) => a.objectifCode === b.objectifCode
    );

    return investisseurObjectifs;
  }

  public wrapInvestisseurObjectifToInvestisseurFicheObjectifs(
    investisseurObjectifs: InvestisseurObjectifRecueil[]
  ): InvestisseurFicheObjectif[] {
    return investisseurObjectifs.map(obj => {
      return {
        id: obj?.id?.toString(),
        isSelected: false,
        objectifCode: obj?.objectifCode,
        objectifLibelle: obj?.objectifLibelle,
        categorieCode: obj.categorieCode,
        categorieLibelle: obj.categorieLibelle,
      };
    });
  }
}
